import { Link } from "react-router-dom";
import { AlertTriangle, CalendarRange, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { StaxLiveControl } from "./StaxLiveControl";
import { UsageRunForm } from "./UsageRunForm";
import { RunsList } from "./RunsList";
import { fmtDayY, fmtTime, money, num } from "./meta";

function SetupWarnings({ ov }) {
  const list = [
    !ov.webcdr.configured && <>The WebCDR login is missing — add an API connection with alias <b>webcdr</b> in <Link className="underline" to="/settings?tab=api-connections">Settings → API Connections</Link>.</>,
    !ov.connection && <>Pick the billing database (ucbilling) under <Link className="underline" to="/billing-sync/usage-charges/settings">Settings</Link>.</>,
    !ov.mirror_configured && <>The FusebillSync mirror is not set in Billing Sync → Settings, so Stax Bill cannot be checked.</>,
  ].filter(Boolean);
  if (!list.length) return null;
  return (
    <div className="space-y-1 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900" data-testid="uc-setup-warnings">
      {list.map((w, i) => <div key={i} className="flex items-start gap-2"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span>{w}</span></div>)}
    </div>
  );
}

function CycleCard({ ov, reload }) {
  const c = ov.open_cycle;
  const u = ov.latest_usage?.cycle?.key === c.key ? ov.latest_usage : null;
  const span = (new Date(c.end) - new Date(c.start)) / 864e5 + 1;
  const done = Math.max(0, Math.min(span, (new Date(c.through) - new Date(c.start)) / 864e5 + 1));
  return (
    <Card className="overflow-hidden border-slate-200 shadow-sm" data-testid="uc-cycle-card">
      <div className="grid gap-0 md:grid-cols-[1.3fr_1fr_1fr]">
        <div className="border-b border-slate-100 px-5 py-4 md:border-b-0 md:border-r">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400"><CalendarRange className="h-3.5 w-3.5 text-emerald-600" /> Open billing cycle</div>
          <div className="mt-1 font-heading text-xl font-semibold text-slate-900" data-testid="uc-cycle-label">{c.label}</div>
          <div className="text-sm text-slate-600" data-testid="uc-cycle-dates">{fmtDayY(c.start)} → {fmtDayY(c.end)}</div>
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${(done / span) * 100}%` }} /></div>
          <div className="mt-1 text-[11px] text-slate-500">Day {done} of {span} · the daily run collects up to {fmtDayY(c.through)} (yesterday, UTC)</div>
        </div>
        <div className="border-b border-slate-100 px-5 py-4 md:border-b-0 md:border-r" data-testid="uc-cycle-usage">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Usage so far</div>
          {u ? (<>
            <div className="mt-1 font-mono text-xl font-semibold tabular-nums text-slate-900" data-testid="uc-cycle-total">{money(u.usage.total)}</div>
            <div className="text-xs text-slate-500">{num(u.usage.domains)} domains · through {fmtDayY(u.cycle.through)}</div>
            {u.usage.jumps > 0 && <Link to={`runs/${u.id}`} className="mt-1 block text-[11px] font-semibold text-amber-700 hover:underline" data-testid="uc-cycle-jumps">{num(u.usage.jumps)} unusual change(s) vs {u.cycle.previous?.label} · {num(Math.max(u.usage.jumps - (u.reviews?.reviewed || 0), 0))} not reviewed</Link>}
            {u.cycle.empty_days?.length > 0 && <div className="mt-1 text-[11px] text-amber-700" data-testid="uc-cycle-empty-days">{u.cycle.empty_days.length} day(s) of this cycle have no calls saved yet</div>}
            <Link to={`runs/${u.id}`} className="mt-1 inline-block text-xs font-medium text-emerald-700 underline" data-testid="uc-cycle-open-run">Open the amounts ({fmtTime(u.finished_at)})</Link>
          </>) : <p className="mt-1 text-xs text-slate-500">No usage check for this cycle yet — run one below.</p>}
        </div>
        <StaxLiveControl ov={ov} reload={reload} />
      </div>
    </Card>
  );
}

function Running({ run }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-5 py-4" data-testid="uc-running">
      <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
        <Loader2 className="h-4 w-4 animate-spin text-emerald-600" /> Run in progress — {run.step}
        <span className="ml-auto font-mono text-xs tabular-nums text-slate-500" data-testid="uc-running-pct">{run.pct || 0}%</span>
      </div>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-emerald-500 transition-[width] duration-700" style={{ width: `${Math.max(run.pct || 0, 3)}%` }} /></div>
      <p className="mt-2 text-[11px] text-slate-500">Started {fmtTime(run.started_at)} by {run.started_by_name}. You can leave this page. <Link to={`runs/${run.id}`} className="font-medium text-emerald-700 underline" data-testid="uc-running-open">Watch the log</Link></p>
    </div>
  );
}

export function UsageOverview({ ov, reload }) {
  return (
    <div className="space-y-4" data-testid="uc-overview">
      <SetupWarnings ov={ov} />
      <CycleCard ov={ov} reload={reload} />
      {ov.running && <Running run={ov.running} />}
      {ov.can_edit && <UsageRunForm ov={ov} onStarted={reload} />}
      <RunsList key={ov.running?.id || "idle"} />
    </div>
  );
}
